/** Shared helpers for Uptime and VPN ping line charts (same monitor API shape). */

/** History presets for uptime + VPN charts (vpn has no upstream interval; timestamp window only). */
export const MONITOR_HISTORY_RANGE_OPTIONS = ["1h", "3h", "6h", "1d", "1w"];

export function getUTCTimeFromRange(range) {
  const now = new Date();

  const ranges = {
    "1h": 60 * 60 * 1000,
    "3h": 3 * 60 * 60 * 1000,
    "6h": 6 * 60 * 60 * 1000,
    "1d": 24 * 60 * 60 * 1000,
    "1w": 7 * 24 * 60 * 60 * 1000,
  };

  const offset = ranges[range];
  if (!offset) throw new Error(`Unsupported range: ${range}`);

  const pastTime = new Date(now.getTime() - offset);
  return pastTime.toISOString();
}

/**
 * Normalizes monitor list items (VPN ping payloads may nest like uptime or use snake_case).
 */
export function normalizeMonitorRowsForUptimeChart(rows) {
  if (!Array.isArray(rows)) return [];

  return rows
    .map((item) => {
      const nested =
        item?.vpn_ping ??
        item?.uptime ??
        item?.vpnping ??
        (item?.timestamp != null ||
        item?.responseTime != null ||
        item?.response_time != null ||
        item?.latencyMs != null ||
        item?.latency_ms != null
          ? item
          : null);
      if (!nested) return null;
      const ts = nested.timestamp ?? nested.time;
      if (ts == null) return null;
      return {
        uptime: {
          ...nested,
          timestamp: ts,
          responseTime:
            nested.responseTime ??
            nested.response_time ??
            nested.latencyMs ??
            nested.latency_ms,
        },
        interface_traffic: item?.interface_traffic,
      };
    })
    .filter(Boolean);
}

export function formatIncomingData(data) {
  if (!Array.isArray(data)) return { data: [], errorAreas: [] };

  const reversedData = data.slice().reverse();

  const formattedData = reversedData
    .map((item) => {
      const uptime = item?.uptime;
      const responseRaw =
        uptime?.responseTime ??
        uptime?.response_time ??
        uptime?.latencyMs ??
        uptime?.latency_ms;
      const value =
        responseRaw != null && responseRaw !== -1
          ? parseFloat(Number(responseRaw).toFixed(2))
          : null;
      const traffic = item?.interface_traffic?.[0];
      const rxBytes = traffic?.rxBytes ?? traffic?.rx_bytes;
      const txBytes = traffic?.txBytes ?? traffic?.tx_bytes;
      const rx = rxBytes != null ? rxBytes / 1024 / 1024 : null;
      const tx = txBytes != null ? txBytes / 1024 / 1024 : null;
      const ts = uptime?.timestamp ?? uptime?.time;
      if (ts == null) return null;
      return {
        timestamp: new Date(ts).toISOString(),
        responseTime: value,
        transmitData:
          tx != null && !isNaN(tx) && tx !== -1 ? Number(tx).toFixed(2) : null,
        receivedData:
          rx != null && !isNaN(rx) && rx !== -1 ? Number(rx).toFixed(2) : null,
      };
    })
    .filter(Boolean);

  const areas = [];
  let startIndex = null;

  for (let i = 0; i < formattedData.length; i++) {
    if (formattedData[i].responseTime === null) {
      if (startIndex === null) startIndex = i;
    } else {
      if (startIndex !== null) {
        const prevIndex = startIndex - 1;
        const nextIndex = i;
        const x1 =
          prevIndex >= 0
            ? formattedData[prevIndex].timestamp
            : formattedData[startIndex].timestamp;
        const x2 = formattedData[nextIndex].timestamp;

        areas.push({ x1, x2, label: formattedData[i].timestamp });
        startIndex = null;
      }
    }
  }

  if (startIndex !== null) {
    const prevIndex = startIndex - 1;
    const x1 =
      prevIndex >= 0
        ? formattedData[prevIndex].timestamp
        : formattedData[startIndex].timestamp;
    const x2 = formattedData[formattedData.length - 1].timestamp;
    areas.push({ x1, x2, label: x1 });
  }

  return {
    data: formattedData,
    errorAreas: areas,
  };
}
